import React, { useMemo, useState, useEffect } from 'react';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { prettyTextToKebabCase } from '@repo/utils';
import { Text } from '@uhg-abyss/web/ui/Text';
import { CategoryData } from './CboCategories';

export type CboSubcategoryModalProps = {
  // Determines both what subcategories we show as our modal content,
  //  AND the title of this modal.
  currentTopLevelCategory: CategoryData;

  onClose: () => void;

  // Let the Search component define what occurs when the user selects a category from the modal,
  //  but we expect that it will trigger a new program search with the selected category.
  onClickCategoryFilter: (clicked: CategoryData) => void;
  currentZipcode?: string;
};

export const CboSubcategoryModal = ({
  currentTopLevelCategory,
  onClose,
  onClickCategoryFilter,
  currentZipcode
}: CboSubcategoryModalProps) => {
  // The children of this category are what we will show within the modal (henceforth referred to as 'sub-categories').
  // If the user selects 'All' from the filters, this `currentCategoryData` object is what is returned.
  // If the user clicks one of the sub-categories, we will return the `child` of this object
  //  which was clicked.

  // Cache the flattened taxonomy, we don't need to re-compute this unless the current category data changes.

  const flattenedCategoryData: CategoryData[] = useMemo(() => {
    return !!currentTopLevelCategory
      ? flattenRecursiveCategoryChildren(currentTopLevelCategory).sort(
          (a, b) => {
            return a.label.localeCompare(b.label);
          }
        )
      : [];
  }, [currentTopLevelCategory]);



  // State of the user's typed-in filter query.
  const [filterByQueryValue, setFilterByQueryValue] = useState<string>('');
  const [zipcode, setZipcode] = useState<string>('');



  return (
    <React.Fragment>
      <ModalDialog
        header={currentTopLevelCategory.label}
        isOpen={currentTopLevelCategory !== undefined}
        onClose={() => {
          setFilterByQueryValue('');
          onClose();
        }}
        css={{
          'abyss-modal-content-container': {
            padding: '16px',
          },
        }}
      >
        <div>
          <TextInput
            label="Filter"
            data-cy="filter-categories-input"
            value={filterByQueryValue}
            placeholder={`Filter ${currentTopLevelCategory?.label} Needs`}
            onChange={(e) => {
              return setFilterByQueryValue(e.target.value);
            }}
            onClear={() => {
              return setFilterByQueryValue('');
            }}
          />
        </div>

        <div
          style={{
            padding: '8px',
          }}
        >
          <Grid
            style={{
              margin: '0px',
            }}
          >
            {/* 'All' is a special case and will always take up a full visual row */}
            <Grid.Col
              span={{
                xs: 12,
              }}
            >
              <Text
                color="#196ecf"
                fontWeight="bold"
                className="commoncategoryHover cursor-pointer"
                onClick={() => {
                  setFilterByQueryValue(''); // Clear the filter query when the user selects something
                  onClickCategoryFilter(currentTopLevelCategory);
                }}
                data-cy={`select-all-${prettyTextToKebabCase(
                  currentTopLevelCategory.label ?? ''
                )}-filters-btn`}
                fontSize="sm"
              >
                All
              </Text>
            </Grid.Col>
            {flattenedCategoryData
              .filter((subcategory: CategoryData) =>
                // If the user has entered a query to filter by, then we will only render
                //  links that include that query.
                // If there's no query value, then render all of them (return true from this predicate).
                {
                  return filterByQueryValue !== ''
                    ? subcategory.label
                        .toLowerCase()
                        .includes(filterByQueryValue.toLowerCase())
                    : true;
                }
              )
              .map((subcategory: CategoryData) => {
                return (
                  <Grid.Col key={subcategory.id}>
                    <Text
                      color="#196ecf"
                      fontWeight="bold"
                      className="commoncategoryHover cursor-pointer"
                      // FIXME - Type error on this prop
                      onClick={() => {
                        setFilterByQueryValue(''); // Clear the filter query when the user selects something
                        onClickCategoryFilter(subcategory);
                      }}
                      fontSize="sm"
                      data-cy={`select-${prettyTextToKebabCase(
                        subcategory.label
                      )}-filters-btn`}
                    >
                      {subcategory.label.charAt(0).toUpperCase() +
                        subcategory.label.slice(1).toLowerCase()}
                    </Text>
                  </Grid.Col>
                );
              }, [])}
          </Grid>
        </div>
      </ModalDialog>
    </React.Fragment>
  );
};

function flattenRecursiveCategoryChildren(
  category: CategoryData
): CategoryData[] {
  if (!category.children) return [];

  return category.children?.flatMap((subcategory: CategoryData) => {
    return [subcategory, ...flattenRecursiveCategoryChildren(subcategory)];
  });
}
